import type { SimpleTask } from 'Automations/nodeDefinitions/conductor'
import type {
  ResolutionContext,
  ResolvedTaskData
} from '../../simulation.types'
import { isRecord } from './isRecord'
import { resolveValue } from './resolveValue'

/**
 * Fills in a task's input `data` from what we already know, ready to be yeeted
 * over to the service to invoke the task executor.
 *
 * Given a context of:
 *
 *   workflowInput: { quoteId: 'quote-1' }
 *   taskOutputs:   { getQuote: { quote: { customerId: 'cus-1' } } }
 *
 * each kind of value reads like this:
 *
 *   '${workflow.input.quoteId}'          -> 'quote-1'
 *   '${getQuote.output.quote.customerId}' -> 'cus-1'
 *   'PUBLISH_QUOTE'                      -> 'PUBLISH_QUOTE'
 *   '${workflow.input.comment}'          -> nothing there, so unresolved
 */
export const resolveTaskData = ({
  task,
  context
}: {
  task: SimpleTask
  context: ResolutionContext
}): ResolvedTaskData => {
  const data = task.inputParameters.data
  if (!isRecord(data)) return { data: {} }

  return Object.entries(data).reduce<ResolvedTaskData>(
    (acc, [key, value]) => {
      if ('unresolved' in acc) return acc

      const resolved = resolveValue({ value, context })
      if ('unresolved' in resolved) return resolved

      return { data: { ...acc.data, [key]: resolved.value } }
    },
    { data: {} }
  )
}
